import type {
  FetchArgs,
  FetchBaseQueryError
} from "@reduxjs/toolkit/query/react";

export const TIMEOUT_MESSAGE_DEFAULT =
  "The request took longer than expected and has timed out. Please try again.";
export const TIMEOUT_MESSAGE_REPORTS =
  "This is taking longer than expected. Try a smaller date range or fewer filters and try again.";
export const NETWORK_ERROR_MESSAGE =
  "Network error — check your connection and try again.";
export const MAX_TIMEOUT_RETRIES = 2;

// Transient timeouts worth retrying. Excludes FETCH_ERROR (DNS / CORS / offline
// rarely self-heal) and 504 (server-side slow query repeats).
export const isRetryableNetworkError = (
  error?: FetchBaseQueryError
): boolean => {
  if (!error) return false;
  return error.status === "TIMEOUT_ERROR" || error.status === 0;
};

// All timeout-like errors that should surface to the user.
export const isTimeoutLikeError = (error?: FetchBaseQueryError): boolean => {
  if (!error) return false;
  return error.status === 504 || isRetryableNetworkError(error);
};

// Distinct from timeouts: DNS / CORS / offline. Surfaced with different copy.
export const isFetchError = (error?: FetchBaseQueryError): boolean => {
  return error?.status === "FETCH_ERROR";
};

export const getErrorMessage = (error: FetchBaseQueryError): string => {
  if (isFetchError(error)) return NETWORK_ERROR_MESSAGE;
  const pathname = window.location.pathname;
  const isReportsOrDashboard =
    pathname.startsWith("/reports/") || pathname.startsWith("/dashboard/");
  return isReportsOrDashboard
    ? TIMEOUT_MESSAGE_REPORTS
    : TIMEOUT_MESSAGE_DEFAULT;
};

export const getRequestMethod = (args: string | FetchArgs): string => {
  if (typeof args === "string") return "GET";
  return (args.method ?? "GET").toUpperCase();
};
